import * as z from 'zod';
import {
  dateRangeSchema,
  timeFormatHoursMinutesSecondsSchema,
} from '@ais-ui/utils';
import { timeStringToSeconds } from '../../utils/timeStringToSeconds';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  MessageSchedulePutRequest,
  MessageSchedulePutRequestGeometry,
  Pss,
  Schedule,
} from '@ais-ui/api-spec/config';
import { Group } from '@ais-ui/websockets';
import { secondsToTimeString } from '../../utils/secondsToTimeString';
import { SelectType } from '@ais-ui/ui-core';
import { ScheduleWithDate } from '@ais-ui/api';

export const scheduleMessageFormSchema = z
  .object({
    id: z.string().optional(),
    enabled: z.boolean(),
    messageType: z.string(),
    noticeType: z.string().optional(),
    action: z.string().optional(),
    shapeId: z.string().optional(),
    linkShapeToShipPosition: z.boolean().optional(),
    sender: z.string(),
    name: z.string(),
    message: z.string(),
    pss: z
      .array(
        z.object({
          value: z.string(),
          label: z.string(),
        })
      )
      .min(1, 'There must be at least one base station sending the message.'),
    schedule: z.array(dateRangeSchema),
    broadcastInterval: timeFormatHoursMinutesSecondsSchema,
    selectedGroups: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    ),
  })
  .refine(
    (data) => {
      // If messageType is 'geographicnotice', shapeId is required
      if (data.messageType === 'geographicnotice') {
        return data.shapeId !== undefined && data.shapeId !== '';
      }
      // For all other cases, shapeId is not required
      return true;
    },
    {
      message: 'shapeId is required when messageType is geographicnotice',
      path: ['shapeId'], // This will attach the error to the shapeId field
    }
  );

export type ScheduleMessageFormValues = z.infer<
  typeof scheduleMessageFormSchema
>;

export const convertScheduleMessageValuesToBackend = (
  data: ScheduleMessageFormValues,
  shape: GeoJson | undefined
): MessageSchedulePutRequest => {
  const filteredSchedule = data.schedule
    .filter((dateRange) => dateRange.start && dateRange.end)
    .map((dateRange) => ({
      start: dateRange.start?.toISOString() as string,
      end: dateRange.end?.toISOString() as string,
    }));

  return {
    uid: data.id,
    enabled: data.enabled,
    addressed: false,
    schedMsgName: data.name,
    type: data.messageType,
    noticeDescription: data?.noticeType ? parseInt(data.noticeType) : undefined,
    shapeuid: data.shapeId,
    geometry: shape
      ? (shape.geometry as MessageSchedulePutRequestGeometry)
      : undefined,
    action: data?.action ? data.action : '',
    text: data.message,
    senderMmsi: data.sender,
    routing: data.pss.map((pss) => pss.value),
    schedule: filteredSchedule,
    broadcastInterval: timeStringToSeconds(data.broadcastInterval),
    status: '',
    groups: data.selectedGroups.map((group) => group.value),
    safety: false,
  };
};

export const grabScheduleInitialPss = (
  schedule: ScheduleWithDate | undefined,
  pss: Pss[] | null | undefined
): SelectType[] => {
  const schedulePss = schedule?.routing || [];

  const matchingPSS =
    pss?.filter(
      (pssItem) =>
        schedulePss.includes(pssItem.uuid as string) ||
        schedulePss.includes(pssItem.name as string)
    ) || [];

  let initialPssSelectables: SelectType[] = [];
  if (matchingPSS) {
    initialPssSelectables = matchingPSS.map((pssItem) => ({
      label: pssItem.name as string,
      value: pssItem.uuid as string,
    }));
  }

  return initialPssSelectables;
};

export const grabScheduleInitialGroups = (
  schedule: ScheduleWithDate | undefined,
  groups: Group[] | null | undefined
): SelectType[] => {
  const scheduleGroups = schedule?.groups || [];

  const matchingGroups =
    groups?.filter(
      (group) =>
        scheduleGroups.includes(group.uuid) ||
        scheduleGroups.includes(group.name)
    ) || [];

  let initialGroupSelectables: SelectType[] = [];
  if (matchingGroups) {
    initialGroupSelectables = matchingGroups.map((group) => ({
      label: group.name,
      value: group.uuid,
    }));
  }

  return initialGroupSelectables;
};

export const convertBackendToMessageScheduleValues = (
  schedule: ScheduleWithDate,
  groups: Group[] | null | undefined,
  pss: Pss[] | null | undefined
): ScheduleMessageFormValues => {
  const scheduleDates = schedule?.schedule
    ? schedule.schedule.map((dateRange) => ({
        start: dateRange.start,
        end: dateRange.end,
      }))
    : [];

  return {
    id: schedule.uid as string,
    enabled: schedule?.enabled || false,
    messageType: schedule?.type || '',
    noticeType: schedule?.noticeDescription
      ? schedule.noticeDescription?.toString()
      : '',
    action: schedule.action,
    shapeId: schedule.shapeuid as string,
    linkShapeToShipPosition: false, // This value is not available in the backend data
    sender: schedule?.senderMmsi ? schedule.senderMmsi.toString() : '',
    name: schedule.schedMsgName as string,
    message: schedule.text as string,
    pss: grabScheduleInitialPss(schedule, pss),
    schedule: scheduleDates,
    broadcastInterval: secondsToTimeString(
      schedule.broadcastInterval as number
    ),
    selectedGroups: grabScheduleInitialGroups(schedule, groups),
  };
};
